import { useMemo, useState } from 'preact/hooks';
import { useIsWide } from '../lib/useIsWide';
import type { JSX } from 'preact';
import { state, mutate, slug, addMealToSlot, removeMealFromSlot, ensureWeek } from '../store/store';
import { Layout } from '../components/Layout';
import { DayCard } from '../components/DayCard';
import { WeekGrid } from '../components/WeekGrid';
import { BalanceBanner } from '../components/BalanceBanner';
import { PickyEaterBanner } from '../components/PickyEaterBanner';
import { CopyWeekModal } from '../components/CopyWeekModal';
import { computeBalance } from '../lib/balance';
import { shouldNudgePickyEater, pickUntried } from '../lib/pickyEater';
import { suggestFillsForSlots, slotKey } from '../lib/suggest';
import { dismissalKey } from '../lib/dismissals';
import { addDays, todayIso, weekIdOf, formatWeekRange } from '../lib/dates';
import { navigate } from '../router';
import type { MealType, Rating, Week } from '../types';


export function PlanPage(props: { weekId?: string }) {
  const s = state.value!;
  const today = todayIso();
  const weekId = props.weekId ?? weekIdOf(today);
  const week = useMemo(() => ensureWeek(s, weekId), [s, weekId]);
  const dates = Array.from({ length: 5 }, (_, i) => addDays(week.start_date, i));
  const enabled: MealType[] = s.settings.meal_types_enabled;

  const recipeById = (id: string) => s.recipes.find(r => r.id === id);

  const balances = enabled.map(mt => ({ mealType: mt, balance: computeBalance(s, weekId, mt) }));
  const nudge = shouldNudgePickyEater(s, weekId);

  const balanceDismissKey = dismissalKey(slug.value, `balance-${weekId}`);
  const pickyDismissKey = dismissalKey(slug.value, `picky-${weekId}`);
  // Bumped on dismiss to force re-read from localStorage
  const [, setDismissTick] = useState(0);
  const [copyOpen, setCopyOpen] = useState(false);
  const balanceDismissed = (() => {
    try { return localStorage.getItem(balanceDismissKey) === '1'; } catch { return false; }
  })();
  const pickyDismissed = (() => {
    try { return localStorage.getItem(pickyDismissKey) === '1'; } catch { return false; }
  })();
  function dismissBalance() {
    try { localStorage.setItem(balanceDismissKey, '1'); } catch {}
    setDismissTick(t => t + 1);
  }
  function dismissPicky() {
    try { localStorage.setItem(pickyDismissKey, '1'); } catch {}
    setDismissTick(t => t + 1);
  }

  async function rate(date: string, mealType: MealType, recipeId: string, r: Rating) {
    await mutate(prev => {
      const w = ensureWeek(prev, weekId);
      const meals = w.meals.map(m =>
        (m.date === date && m.meal_type === mealType && m.recipe_id === recipeId)
          ? { ...m, rating: r, rated_at: new Date().toISOString() }
          : m
      );
      const updated: Week = { ...w, meals };
      const weeks = prev.weeks.some(x => x.id === weekId)
        ? prev.weeks.map(x => x.id === weekId ? updated : x)
        : [...prev.weeks, updated];
      return { ...prev, weeks };
    });
  }

  function tapSlot(date: string, mealType: MealType) {
    navigate(`/h/${slug.value}/recipes?slot=${date}:${mealType}`);
  }

  function clearSlot(date: string, mealType: MealType, recipeId: string) {
    void removeMealFromSlot(weekId, date, mealType, recipeId);
  }

  function openRecipe(recipeId: string) {
    navigate(`/h/${slug.value}/recipes/${recipeId}`);
  }

  // Per-meal-type local-time cutoffs for "this slot is in the past, don't auto-fill it".
  // Hours are in the user's local timezone. After this hour on `today`, the meal is treated as past.
  const PAST_CUTOFF_HOUR: Record<MealType, number> = {
    breakfast: 10,
    lunch: 14,
    snack: 17,
    dinner: 20,
  };

  function isSlotInPast(date: string, mealType: MealType): boolean {
    if (date < today) return true;
    if (date > today) return false;
    const now = new Date();
    return now.getHours() >= PAST_CUTOFF_HOUR[mealType];
  }

  function onSuggestUntried() {
    const empty = enabled
      .flatMap(mt => dates.map(d => ({ date: d, meal_type: mt })))
      .filter(s2 => !isSlotInPast(s2.date, s2.meal_type))
      .find(s2 => !week.meals.some(m => m.date === s2.date && m.meal_type === s2.meal_type));
    if (!empty) return;
    const r = pickUntried(s, empty.meal_type);
    if (r) void addMealToSlot(weekId, empty.date, empty.meal_type, r.id);
  }

  async function suggestFills() {
    const todo = enabled
      .flatMap(mt => dates.map(d => ({ date: d, meal_type: mt })))
      .filter(s2 => !isSlotInPast(s2.date, s2.meal_type))
      .filter(s2 => !week.meals.some(m => m.date === s2.date && m.meal_type === s2.meal_type));
    if (todo.length === 0) return;
    const fills = suggestFillsForSlots(s, todo);
    if (fills.size === 0) return;
    // Apply all picks in a single mutation (one PUT, atomic).
    await mutate(prev => {
      const w = ensureWeek(prev, weekId);
      const newMeals = [...w.meals];
      for (const slot of todo) {
        const recipe = fills.get(slotKey(slot));
        if (!recipe) continue;
        newMeals.push({
          date: slot.date, meal_type: slot.meal_type,
          recipe_id: recipe.id, rating: null, rated_at: null, note: '',
        });
      }
      const updated: Week = { ...w, meals: newMeals };
      const weeks = prev.weeks.some(x => x.id === weekId)
        ? prev.weeks.map(x => x.id === weekId ? updated : x)
        : [...prev.weeks, updated];
      return { ...prev, weeks };
    });
  }

  const isWide = useIsWide();
  const dayName = (d: string) => new Date(d + 'T00:00:00Z').toLocaleDateString(undefined, { weekday: 'short', timeZone: 'UTC' });

  const plannedCount = week.meals.filter(m => m.recipe_id).length;
  const isWeekEmpty = plannedCount === 0;
  const hasPriorPlannedWeeks = s.weeks.some(w => w.id !== weekId && w.meals.some(m => m.recipe_id));

  return (
    <Layout active="plan" slug={slug.value}>
      <CopyWeekModal
        open={copyOpen}
        currentWeekId={weekId}
        onClose={() => setCopyOpen(false)}
      />
      <div style={{ padding: isWide ? '20px 24px' : '12px 14px' }}>
        {!isWeekEmpty && s.settings.show_balance_check && !balanceDismissed && (
          <BalanceBanner balances={balances} onDismiss={dismissBalance} />
        )}
        {!isWeekEmpty && <div style={{ height: 'var(--space-md)' }} />}
        {!isWeekEmpty && s.settings.show_picky_eater_nudge && !pickyDismissed && (
          <PickyEaterBanner visible={nudge} onSuggestUntried={onSuggestUntried} onDismiss={dismissPicky} />
        )}

        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginBottom: 'var(--space-lg)', flexWrap: 'wrap', gap: 8,
        }}>
          <h2 style={{ margin: 0, fontSize: 22 }}>This week · {formatWeekRange(week.start_date)}</h2>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <button onClick={() => navigate(`/h/${slug.value}/plan/${addDays(weekId, -7)}`)} style={smallBtn}>← Prev</button>
            <button onClick={() => navigate(`/h/${slug.value}/plan/${addDays(weekId, 7)}`)} style={smallBtn}>Next →</button>
            {!isWeekEmpty && (
              <button
                onClick={() => setCopyOpen(true)}
                disabled={!hasPriorPlannedWeeks}
                style={{ ...smallBtn, opacity: hasPriorPlannedWeeks ? 1 : 0.5 }}
                title="Copy meals from a previous week into empty slots"
              >↺ Copy from week</button>
            )}
            <button
              onClick={() => navigate(`/h/${slug.value}/shopping/${weekId}`)}
              style={smallBtn}
            >🛒 Shopping list</button>
            {!isWeekEmpty && (
              <button onClick={suggestFills} style={primarySmallBtn}>✨ Suggest meals</button>
            )}
          </div>
        </div>

        {isWeekEmpty ? (
          <div style={{
            marginTop: 32, padding: '48px 24px', textAlign: 'center',
            background: 'white', borderRadius: 'var(--radius-lg)',
            border: '1px solid var(--hairline)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
          }}>
            <div style={{ fontSize: 32 }}>🍽️</div>
            <h3 style={{ margin: 0, fontSize: 18 }}>Nothing planned yet</h3>
            <p style={{ margin: 0, color: 'var(--muted)', fontSize: 14, maxWidth: 360 }}>
              Browse the recipe library and add meals to days, or copy a plan from a previous week.
            </p>
            <div style={{ display: 'flex', gap: 10, marginTop: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
              <button
                onClick={() => navigate(`/h/${slug.value}/recipes`)}
                style={primaryBtn}
              >Browse recipes →</button>
              <button
                onClick={() => setCopyOpen(true)}
                disabled={!hasPriorPlannedWeeks}
                style={{ ...secondaryBtn, opacity: hasPriorPlannedWeeks ? 1 : 0.5 }}
              >↺ Copy from another week</button>
            </div>
          </div>
        ) : isWide ? (
          <WeekGrid
            startDate={week.start_date} todayIso={today}
            enabledMealTypes={enabled} dates={dates}
            meals={week.meals} recipeById={recipeById}
            onRate={rate} onTapSlot={tapSlot}
            onClearSlot={clearSlot} onOpenRecipe={openRecipe}
          />
        ) : (
          dates.map(d => (
            <DayCard
              key={d}
              date={d} dayName={dayName(d)} isToday={d === today}
              meals={week.meals.filter(m => m.date === d)}
              enabledMealTypes={enabled} recipeById={recipeById}
              onRate={rate} onTapSlot={tapSlot}
            onClearSlot={clearSlot} onOpenRecipe={openRecipe}
            />
          ))
        )}
      </div>
    </Layout>
  );
}

const smallBtn: JSX.CSSProperties = {
  padding: '7px 12px', height: 32, fontSize: 12, borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--hairline)', background: 'white',
  color: 'var(--ink)', fontWeight: 600, cursor: 'pointer', lineHeight: 1,
};

const primarySmallBtn: JSX.CSSProperties = {
  padding: '8px 14px', height: 36, fontSize: 13, borderRadius: 'var(--radius-sm)',
  border: 'none', background: 'var(--pink-primary)',
  color: 'var(--on-primary)', fontWeight: 600, cursor: 'pointer', lineHeight: 1,
};

const primaryBtn: JSX.CSSProperties = {
  padding: '10px 18px', fontSize: 14, borderRadius: 'var(--radius-md)',
  border: 'none', background: 'var(--pink-primary)',
  color: 'var(--on-primary)', fontWeight: 600, cursor: 'pointer', lineHeight: 1,
};

const secondaryBtn: JSX.CSSProperties = {
  padding: '10px 18px', fontSize: 14, borderRadius: 'var(--radius-md)',
  border: '1px solid var(--hairline)', background: 'white',
  color: 'var(--ink)', fontWeight: 600, cursor: 'pointer', lineHeight: 1,
};
